import React, { useState } from "react";

interface AdminLoginProps {
  onLogin: (e: React.FormEvent, email: string, pass: string) => void;
  loading: boolean;
}

export const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin, loading }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const inputStyle: React.CSSProperties = {
    width: "100%",
    background: "rgba(10, 10, 10, 0.8)",
    color: "#f9eeda",
    border: "1px solid #1b4d52",
    padding: "18px",
    marginBottom: "20px",
    fontFamily: "'Oswald', sans-serif",
    fontSize: "1.2rem",
    textTransform: "uppercase",
    letterSpacing: "2px",
    boxSizing: "border-box",
    outline: "none",
    transition: "border 0.3s ease",
  };

  const btnStyle: React.CSSProperties = {
    width: "100%",
    background: "linear-gradient(90deg, #1b4d52 0%, #22565a 100%)",
    color: "#f9eeda",
    border: "none",
    padding: "22px",
    cursor: "pointer",
    fontFamily: "'Oswald', sans-serif",
    fontWeight: "900",
    fontSize: "1.8rem",
    textTransform: "uppercase",
    letterSpacing: "8px",
    marginTop: "20px",
    boxShadow: "0 0 30px rgba(34, 86, 90, 0.4)",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        margin: 0,
        padding: 0,
        backgroundColor: "#020202",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Oswald', sans-serif",
        position: "fixed", // Ensures it covers the entire viewport without scrollbars
        top: 0,
        left: 0,
        overflow: "hidden",
      }}
    >
      <style>
        {`
          @keyframes stadiumGlow {
            0% { opacity: 0.4; }
            50% { opacity: 0.7; }
            100% { opacity: 0.4; }
          }
          @keyframes flareMove {
            0% { transform: translateX(-100%) skewX(-15deg); }
            100% { transform: translateX(200%) skewX(-15deg); }
          }
          @keyframes floatLogo {
            0% { transform: translateY(0px); filter: drop-shadow(0 0 10px #22565a); }
            100% { transform: translateY(-10px); filter: drop-shadow(0 0 25px #22565a); }
          }
          input:focus {
            border-color: #f9eeda !important;
          }
          .login-btn:hover {
            filter: brightness(1.2);
            letter-spacing: 12px !important;
          }

          /* Mobile view adjustments */
          @media (max-width: 768px) {
            .login-container {
              padding: 20px !important;
              max-width: 90% !important;
            }
            .login-title {
              font-size: 2.5rem !important;
              letter-spacing: 4px !important;
            }
            .login-subtitle {
              letter-spacing: 8px !important;
              margin-bottom: 30px !important;
            }
            .login-btn {
              font-size: 1.4rem !important;
              letter-spacing: 4px !important;
              padding: 18px !important;
            }
            .logo-img {
              height: 100px !important;
            }
            .footer-text {
              margin-top: 30px !important;
              letter-spacing: 3px !important;
              font-size: 0.6rem !important;
            }
          }
        `}
      </style>

      {/* Cinematic Sports Background - Stadium Floodlight Effect */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(circle at 20% -10%, rgba(34, 86, 90, 0.4) 0%, transparent 40%),
            radial-gradient(circle at 80% -10%, rgba(34, 86, 90, 0.4) 0%, transparent 40%),
            linear-gradient(180deg, #050505 0%, #000000 100%)
          `,
          zIndex: 1,
        }}
      />

      {/* Dynamic Action Lines (Speed Graphics) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "repeating-linear-gradient(90deg, transparent, transparent 150px, rgba(34, 86, 90, 0.03) 151px, transparent 152px)",
          zIndex: 2,
        }}
      />

      {/* Sweeping Light Flare */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "30%",
          height: "100%",
          background:
            "linear-gradient(90deg, transparent, rgba(34, 86, 90, 0.05), transparent)",
          animation: "flareMove 8s infinite linear",
          zIndex: 3,
        }}
      />

      {/* Bottom Turf/Field Glow */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          width: "100%",
          height: "20vh",
          background:
            "linear-gradient(0deg, rgba(27, 77, 82, 0.2) 0%, transparent 100%)",
          zIndex: 2,
          animation: "stadiumGlow 4s infinite ease-in-out",
        }}
      />

      <form
        onSubmit={(e) => onLogin(e, email, password)}
        className="login-container"
        style={{
          zIndex: 10,
          width: "100%",
          maxWidth: "460px",
          padding: "40px",
          textAlign: "center",
          position: "relative",
        }}
      >
        {/* Animated Logo */}
        <img
          src="https://dpsrbqvcukoomuupjfdm.supabase.co/storage/v1/object/public/draft%20bucket/Empire%20League%20Logo.png"
          alt="Empire"
          className="logo-img"
          style={{
            height: "140px",
            marginBottom: "20px",
            animation: "floatLogo 4s infinite alternate ease-in-out",
          }}
        />

        <h2
          className="login-title"
          style={{
            color: "#f9eeda",
            fontSize: "4rem",
            fontWeight: 900,
            margin: 0,
            letterSpacing: "8px",
            textTransform: "uppercase",
            lineHeight: 1,
          }}
        >
          EMPIRE LEAGUE
        </h2>
        <p
          className="login-subtitle"
          style={{
            color: "#22565a",
            letterSpacing: "15px",
            fontSize: "0.9rem",
            margin: "15px 0 60px 0",
            fontWeight: "bold",
          }}
        >
          ADMIN LOG IN
        </p>

        <div style={{ position: "relative", marginBottom: "30px" }}>
          <input
            type="email"
            placeholder="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
            required
          />
          <input
            type="password"
            placeholder="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
            required
          />
        </div>

        <button
          type="submit"
          className="login-btn"
          disabled={loading}
          style={btnStyle}
        >
          {loading ? "AUTHENTICATING..." : "sign in"}
        </button>

        <div
          className="footer-text"
          style={{
            marginTop: "60px",
            fontSize: "0.7rem",
            color: "rgba(34, 86, 90, 0.6)",
            letterSpacing: "6px",
            fontWeight: "bold",
            textTransform: "uppercase",
          }}
        >
          Secure Connection Established // Empire League
        </div>
      </form>
    </div>
  );
};
